import { useEffect } from 'react';
import { pulse } from '../../../pulse/floor';
import {
  APP_MANAGER_ID,
  APP_SCOPE_ID,
  registerScope,
  unregisterScope,
} from '../../../pulse/scopeRegistry';
import { useGlobalAppStateStore } from './BaseGlobalAppStateStoreProvider';
import type { BaseGlobalAppStateStoreProviderProps } from './types';

export interface AppPulseScopeRegistrarProps {
  interfaceRecord: BaseGlobalAppStateStoreProviderProps['interfaceRecord'];
}

/**
 * The app-scope counterpart to PagePulseScopeRegistrar: registers the singleton app base scope so
 * its stateChanges/events attribute correctly and its snapshot is available to the State tab.
 *
 * Reads the store from context (useGlobalAppStateStore); it is mounted directly in
 * InterfaceAppProviders below GlobalAppStateStoreProvider so the store is in context. No
 * isPulseEnabled gate here (unlike the page registrar): InterfaceAppProviders swaps in
 * AppPulseScopeRegistrarPassthrough for the runner (the InterfaceModes check), so this never runs
 * there. Effect (not initializer) for StrictMode symmetry — mount→cleanup→remount re-registers.
 */
export function AppPulseScopeRegistrar({ interfaceRecord }: AppPulseScopeRegistrarProps) {
  const store = useGlobalAppStateStore();
  useEffect(() => {
    registerScope(
      {
        scopeId: APP_SCOPE_ID,
        managerId: APP_MANAGER_ID,
        instanceId: null,
        label: interfaceRecord.properties.name,
        kind: 'app',
      },
      () => store.getState(),
    );
    return () => {
      unregisterScope(APP_SCOPE_ID);
      pulse.resetScope(APP_MANAGER_ID);
    };
  }, [store, interfaceRecord]);
  return null;
}

// Runner stand-in for AppPulseScopeRegistrar: registers nothing (no scope, no floor effect). Same
// props so InterfaceAppProviders can swap the two by mode, keeping the builder/runner swap symmetric.
export function AppPulseScopeRegistrarPassthrough(_props: AppPulseScopeRegistrarProps) {
  return null;
}
